import { IDxfBounds, IDxfEntity, IDxfPoint } from './dxf-entities';
import { IDxfSpace, writeEntity, writeViewports } from './dxf-entity-writer';
import { DXF_DEFAULT_LAYER, IDxfLayer } from './dxf-layers';
import { IDxfObjectHandles, writeObjects } from './dxf-objects';
import { IDxfOut, beginSection, dxfOut, endSection, int, pair, point, real, takeHandle } from './dxf-out';
import { IDxfViewport } from './dxf-sheet';
import { DXF_TEXT_STYLE, IDxfBlockOwners, writeTables } from './dxf-tables';

const LINE_END: string = '\r\n';

/** Everything a file holds, already laid out: layers, both spaces, the viewports and the extents. */
export interface IDxfDocument {
    layers: readonly IDxfLayer[];
    /** The views, 1 : 1 in millimetres. */
    model: readonly IDxfEntity[];
    /** The sheet: frame, inscription and what has no millimetre scale. */
    paper: readonly IDxfEntity[];
    viewports: readonly IDxfViewport[];
    /** The box of the model drawing, written as `$EXTMIN`/`$EXTMAX` and opened on. */
    modelExtents: IDxfBounds;
    paperSize: { width: number; height: number };
    /** The name of the sheet's layout tab. */
    sheetName: string;
}

function headerVar(out: IDxfOut, name: string): void {
    pair(out, 9, name);
}

function headerPoint(out: IDxfOut, name: string, at: IDxfPoint): void {
    headerVar(out, name);
    point(out, 10, at);
}

function headerInt(out: IDxfOut, name: string, code: number, value: number): void {
    headerVar(out, name);
    int(out, code, value);
}

function headerText(out: IDxfOut, name: string, code: number, value: string): void {
    headerVar(out, name);
    pair(out, code, value);
}

function writeHeader(out: IDxfOut, document: IDxfDocument, handleSeed: string): void {
    const extents: IDxfBounds = document.modelExtents;
    beginSection(out, 'HEADER');
    headerText(out, '$ACADVER', 1, 'AC1021');
    // The ANSI code page a reader assumes for an older text; a 2007 file keeps its text in UTF-8.
    headerText(out, '$DWGCODEPAGE', 3, 'ANSI_1251');
    headerPoint(out, '$INSBASE', [0, 0]);
    headerPoint(out, '$EXTMIN', [extents.minX, extents.minY]);
    headerPoint(out, '$EXTMAX', [extents.maxX, extents.maxY]);
    headerVar(out, '$LIMMIN');
    point(out, 10, [extents.minX, extents.minY]);
    headerVar(out, '$LIMMAX');
    point(out, 10, [extents.maxX, extents.maxY]);
    headerVar(out, '$LTSCALE');
    real(out, 40, 1);
    headerText(out, '$TEXTSTYLE', 7, DXF_TEXT_STYLE);
    headerText(out, '$CLAYER', 8, DXF_DEFAULT_LAYER);
    headerText(out, '$CELTYPE', 6, 'ByLayer');
    // -1: the lineweight of new objects is the layer's.
    headerInt(out, '$CELWEIGHT', 370, -1);
    headerInt(out, '$LWDISPLAY', 290, 1);
    headerInt(out, '$INSUNITS', 70, 4);
    headerInt(out, '$LUNITS', 70, 2);
    headerInt(out, '$LUPREC', 70, 2);
    headerInt(out, '$MEASUREMENT', 70, 1);
    // Every line carries its view's denominator as its linetype scale (group 48), so the dash
    // lengths are already model lengths: neither paper space nor model space scales them again.
    headerInt(out, '$PSLTSCALE', 70, 0);
    headerInt(out, '$MSLTSCALE', 70, 0);
    headerInt(out, '$TILEMODE', 70, 1);
    headerPoint(out, '$PEXTMIN', [0, 0]);
    headerPoint(out, '$PEXTMAX', [document.paperSize.width, document.paperSize.height]);
    headerVar(out, '$PLIMMIN');
    point(out, 10, [0, 0]);
    headerVar(out, '$PLIMMAX');
    point(out, 10, [document.paperSize.width, document.paperSize.height]);
    headerText(out, '$HANDSEED', 5, handleSeed);
    endSection(out);
}

function writeBlock(out: IDxfOut, name: string, owner: string, paper: boolean): void {
    pair(out, 0, 'BLOCK');
    pair(out, 5, takeHandle(out));
    pair(out, 330, owner);
    pair(out, 100, 'AcDbEntity');
    if (paper) {
        int(out, 67, 1);
    }
    pair(out, 8, DXF_DEFAULT_LAYER);
    pair(out, 100, 'AcDbBlockBegin');
    pair(out, 2, name);
    int(out, 70, 0);
    point(out, 10, [0, 0]);
    pair(out, 3, name);
    pair(out, 1, '');
    pair(out, 0, 'ENDBLK');
    pair(out, 5, takeHandle(out));
    pair(out, 330, owner);
    pair(out, 100, 'AcDbEntity');
    if (paper) {
        int(out, 67, 1);
    }
    pair(out, 8, DXF_DEFAULT_LAYER);
    pair(out, 100, 'AcDbBlockEnd');
}

function reserveObjectHandles(out: IDxfOut): IDxfObjectHandles {
    return {
        root: takeHandle(out),
        groups: takeHandle(out),
        layouts: takeHandle(out),
        modelLayout: takeHandle(out),
        paperLayout: takeHandle(out),
    };
}

/**
 * A DXF 2007 (AC1021) file in millimetres: the header, the symbol tables, the two layout blocks,
 * the entities of model space and of the sheet in paper space, and the objects naming the two
 * layouts. Handles are numbered in writing order, so one input always gives the same bytes.
 */
export function serializeDxf(document: IDxfDocument): string {
    const body: IDxfOut = dxfOut();
    const handles: IDxfObjectHandles = reserveObjectHandles(body);
    beginSection(body, 'CLASSES');
    endSection(body);
    const owners: IDxfBlockOwners = writeTables(body, document.layers, document.modelExtents, handles);
    beginSection(body, 'BLOCKS');
    writeBlock(body, '*Model_Space', owners.modelSpace, false);
    writeBlock(body, '*Paper_Space', owners.paperSpace, true);
    endSection(body);
    beginSection(body, 'ENTITIES');
    const model: IDxfSpace = { owner: owners.modelSpace, paper: false };
    const paper: IDxfSpace = { owner: owners.paperSpace, paper: true };
    for (const entity of document.model) {
        writeEntity(body, entity, model);
    }
    const paperViewport: string = writeViewports(body, document.paperSize, document.viewports, paper);
    for (const entity of document.paper) {
        writeEntity(body, entity, paper);
    }
    endSection(body);
    writeObjects(body, handles, {
        paper: document.paperSize,
        sheetName: document.sheetName,
        modelExtents: document.modelExtents,
        owners,
        paperViewport,
    });
    pair(body, 0, 'EOF');

    // The header names the first free handle, known only once the body is written.
    const header: IDxfOut = dxfOut(body.nextHandle);
    writeHeader(header, document, takeHandle(header));
    return [...header.lines, ...body.lines].join(LINE_END) + LINE_END;
}
